import { useParams, Link, useNavigate } from 'react-router-dom';
import { TermsContent } from '@/components/TermsContent';
import { Button } from '@/components/ui/button';
import { Dumbbell, ArrowLeft, Shield, FileText, ExternalLink, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function LegalPage() {
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();

    const validTypes = ['privacy', 'legal', 'disclaimer', 'terms'];
    const currentType = validTypes.includes(type || '') ? (type as any) : 'privacy';

    const menuItems = [
        { id: 'privacy', label: 'Privacidad', icon: Shield },
        { id: 'legal', label: 'Aviso Legal', icon: FileText },
        { id: 'disclaimer', label: 'Integraciones', icon: ExternalLink },
        { id: 'terms', label: 'Términos', icon: FileText },
    ];

    return (
        <div className="min-h-screen bg-white text-slate-900">
            {/* Minimal Header */}
            <header className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
                <div className="container mx-auto px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 group">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 shadow-lg shadow-orange-600/20">
                            <Dumbbell className="h-6 w-6 text-white" />
                        </div>
                        <span className="text-xl font-bold tracking-tight">GymPro <span className="text-orange-600">SV</span></span>
                    </Link>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="font-bold text-slate-500 hover:text-orange-600"
                        onClick={() => navigate('/')}
                    >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        VOLVER AL INICIO
                    </Button>
                </div>
            </header>

            <main className="container mx-auto px-6 py-12 lg:py-20">
                <div className="flex flex-col lg:flex-row gap-12 max-w-6xl mx-auto">

                    {/* Sidebar Nav */}
                    <aside className="lg:w-64 shrink-0">
                        <div className="sticky top-32 space-y-2">
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 ml-4">
                                DOCUMENTACIÓN LEGAL
                            </h4>
                            {menuItems.map((item) => (
                                <Link
                                    key={item.id}
                                    to={`/legal/${item.id}`}
                                    className={cn(
                                        "flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all",
                                        currentType === item.id
                                            ? "bg-orange-50 text-orange-600 shadow-sm border border-orange-100"
                                            : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                                    )}
                                >
                                    <item.icon className={cn("h-4 w-4", currentType === item.id ? "text-orange-600" : "text-slate-400")} />
                                    {item.label}
                                </Link>
                            ))}

                            <div className="mt-12 p-6 bg-slate-900 rounded-[2rem] text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-24 h-24 bg-orange-600/20 blur-[40px] rounded-full" />
                                <h5 className="font-bold text-xs mb-2 uppercase tracking-widest text-orange-500">Soporte</h5>
                                <p className="text-xs text-slate-400 leading-relaxed mb-4">¿Tienes dudas legales sobre el servicio?</p>
                                <a href="mailto:gymprosv@gmail.com" className="flex items-center gap-2 text-xs font-black hover:text-orange-500 transition-colors">
                                    <Mail className="h-3 w-3" />
                                    ESCRIBIR AHORA
                                </a>
                            </div>
                        </div>
                    </aside>

                    {/* Content Area */}
                    <article className="flex-1 bg-white min-w-0">
                        <div className="max-w-3xl">
                            <TermsContent docType={currentType} variant="public" />
                        </div>
                    </article>
                </div>
            </main>

            {/* Footer Minimal */}
            <footer className="py-12 border-t border-slate-100 bg-slate-50">
                <div className="container mx-auto px-6 text-center">
                    <p className="text-slate-400 text-xs font-medium">
                        © {new Date().getFullYear()} GymPro SV. Cumplimiento Legal y Privacidad Protegida. <br />
                        Hecho con pasión en El Salvador.
                    </p>
                </div>
            </footer>
        </div>
    );
}
